// List commands (v0.1.227) — a task's list and the list-template library (shared/lists.js). Pure; see tasks.js for
// the ctx shape. ctx = { tasks, listTemplates, now, newId }. Every list write is one `update` of the task's `list`
// field (+ updatedAt) — never a status, a time entry or a completion: items are information, not work. The one
// exception is listToTasks, which is explicit: it makes real tasks (subtasks) from the unticked items.
import { set, update, del } from './writes.js';
import { makeTaskDoc } from '../taskdoc.js';
import { makeList, cleanItem, cleanQty, makeListTemplate, itemsFromTemplate, itemLabel, LIST_MAX_ITEMS, LIST_ITEM_MAX } from '../lists.js';

const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());
const needTask = (ctx, id) => { const t = (ctx.tasks || []).find((x) => x.id === id); if (!t) throw new Error(`no task ${id}`); return t; };
const needTpl = (ctx, id) => { const t = (ctx.listTemplates || []).find((x) => x.id === id); if (!t) throw new Error(`no list template ${id}`); return t; };
const listOf = (task) => (task.list && Array.isArray(task.list.items) ? task.list : { items: [] });
const writeList = (ctx, task, list) => update(`tasks/${task.id}`, { list, updatedAt: now(ctx) });
const roomFor = (list, n) => { if (list.items.length + n > LIST_MAX_ITEMS) throw new Error(`a list holds at most ${LIST_MAX_ITEMS} items`); };
const bumpUses = (ctx, tpl) => update(`listTemplates/${tpl.id}`, { uses: (tpl.uses || 0) + 1, lastUsedAt: now(ctx), updatedAt: now(ctx) });

/**
 * A list on a task, or more items on the one it has. input = { taskId, items?: (string | { text, qty?, done? })[],
 * templateId?, name? }. A template's items come after the given ones (already-there texts skipped) and its use counts.
 */
export function addList(ctx, { taskId, items = [], templateId = null, name = null }) {
  const task = needTask(ctx, taskId);
  const cur = listOf(task);
  const tpl = templateId ? needTpl(ctx, templateId) : null;
  const given = makeList({ items }, ctx.newId).items;
  const fromTpl = tpl ? itemsFromTemplate(tpl, ctx.newId, [...cur.items, ...given]) : [];
  const add = [...given, ...fromTpl];
  if (!add.length && !tpl && !name && task.list) throw new Error('nothing to add');
  roomFor(cur, add.length);
  const list = {
    ...(cur.templateId || tpl ? { templateId: cur.templateId || tpl.id } : {}),
    ...(cur.name || name || tpl?.name ? { name: cur.name || String(name || tpl.name).trim() } : {}),
    items: [...cur.items, ...add],
  };
  return { writes: [writeList(ctx, task, list), ...(tpl ? [bumpUses(ctx, tpl)] : [])], list, added: add.length };
}

/** "Add from template" — addList with only a template. */
export function attachTemplate(ctx, { taskId, templateId }) {
  if (!templateId) throw new Error('templateId required');
  return addList(ctx, { taskId, templateId });
}

/** One item more at the bottom (makes the list when there is none). */
export function addListItem(ctx, { taskId, text, qty = null }) {
  const task = needTask(ctx, taskId);
  const it = cleanItem({ text, qty }, ctx.newId);
  if (!it) throw new Error('text required');
  const cur = listOf(task);
  roomFor(cur, 1);
  const list = { ...cur, items: [...cur.items, it] };
  return { writes: [writeList(ctx, task, list)], list, item: it };
}

/** Tick / untick / edit one item: { taskId, itemId, done?, text?, qty? } (qty null clears it). */
export function setListItem(ctx, { taskId, itemId, done, text, qty }) {
  const task = needTask(ctx, taskId);
  const cur = listOf(task);
  const i = cur.items.findIndex((x) => x.id === itemId);
  if (i < 0) throw new Error(`no list item ${itemId}`);
  const it = { ...cur.items[i] };
  if (done !== undefined) it.done = !!done;
  if (text !== undefined) {
    const t = String(text ?? '').trim().replace(/\s+/g, ' ').slice(0, LIST_ITEM_MAX);
    if (!t) throw new Error('an item needs text (remove it instead)');
    it.text = t;
  }
  if (qty !== undefined) { const q = cleanQty(qty); if (q == null) delete it.qty; else it.qty = q; }
  const items = cur.items.slice();
  items[i] = it;
  const list = { ...cur, items };
  return { writes: [writeList(ctx, task, list)], list, item: it };
}

/** Drop one item. */
export function removeListItem(ctx, { taskId, itemId }) {
  const task = needTask(ctx, taskId);
  const cur = listOf(task);
  if (!cur.items.some((x) => x.id === itemId)) throw new Error(`no list item ${itemId}`);
  const list = { ...cur, items: cur.items.filter((x) => x.id !== itemId) };
  return { writes: [writeList(ctx, task, list)], list };
}

/** Drop the ticked items (the list stays, with what's left). */
export function clearTicked(ctx, { taskId }) {
  const task = needTask(ctx, taskId);
  const cur = listOf(task);
  const left = cur.items.filter((x) => !x.done);
  const list = { ...cur, items: left };
  return { writes: [writeList(ctx, task, list)], list, cleared: cur.items.length - left.length };
}

/** Remove the whole list from the task. */
export function removeList(ctx, { taskId }) {
  const task = needTask(ctx, taskId);
  return { writes: [update(`tasks/${task.id}`, { list: null, updatedAt: now(ctx) })] };
}

/**
 * Explicit: the unticked items become work. as = 'subtasks' (children of this task, the default) or 'tasks' (beside it:
 * same project, phase, parent and day). The items leave the list (they're tasks now); ticked ones stay. One group.
 */
export function listToTasks(ctx, { taskId, as = 'subtasks' }) {
  const task = needTask(ctx, taskId);
  const cur = listOf(task);
  const open = cur.items.filter((x) => !x.done);
  if (!open.length) throw new Error('no unticked items');
  const at = now(ctx);
  let scope = ctx.tasks || [];
  const made = [];
  for (const it of open) {
    const base = as === 'tasks'
      ? { parentId: task.parentId || null, sectionId: task.sectionId || null, scheduledDate: task.scheduledDate ?? null, ...(task.departmentId ? { departmentId: task.departmentId } : {}) }
      : { parentId: task.id, scheduledDate: task.scheduledDate ?? null };
    const t = makeTaskDoc({ title: itemLabel(it), projectId: task.projectId || null, priority: task.priority, ...base }, scope, at + made.length, ctx.newId);
    scope = [...scope, t];
    made.push(t);
  }
  const list = { ...cur, items: cur.items.filter((x) => x.done) };
  return { writes: [...made.map((t) => set(`tasks/${t.id}`, t)), update(`tasks/${task.id}`, { list, updatedAt: at })], tasks: made, list };
}

// ---- the library: listTemplates ------------------------------------------------------------------------------

/** Create or replace a list template ({ id?, name, icon?, items, departmentId? }); uses / lastUsedAt kept. */
export function saveListTemplate(ctx, tpl) {
  const t = makeListTemplate(tpl);
  if (!t.name) throw new Error('name required');
  const id = tpl.id || ctx.newId();
  const at = now(ctx);
  const was = (ctx.listTemplates || []).find((x) => x.id === id);
  const doc = { ...t, id, uses: was?.uses ?? t.uses, lastUsedAt: was?.lastUsedAt ?? t.lastUsedAt, createdAt: was?.createdAt || tpl.createdAt || at, updatedAt: at };
  return { writes: [set(`listTemplates/${id}`, doc)], template: doc };
}

export function deleteListTemplate(ctx, { id }) {
  needTpl(ctx, id);
  return { writes: [del(`listTemplates/${id}`)] };
}

/**
 * "Save as template": the task's list (ticks cleared) as a new list template — or over `id`. The task's list then
 * remembers the template it is. input = { taskId, name?, icon?, departmentId?, id? }.
 */
export function saveListAsTemplate(ctx, { taskId, name = null, icon = null, departmentId, id = null }) {
  const task = needTask(ctx, taskId);
  const cur = listOf(task);
  if (!cur.items.length) throw new Error('the list is empty');
  const r = saveListTemplate(ctx, {
    id: id || undefined,
    name: String(name || cur.name || task.title || '').trim(),
    icon,
    items: cur.items.map((x) => ({ text: x.text, ...(x.qty != null ? { qty: x.qty } : {}) })),
    departmentId: departmentId !== undefined ? departmentId : (task.departmentId || null),
  });
  const list = { ...cur, templateId: r.template.id, name: cur.name || r.template.name };
  return { writes: [...r.writes, writeList(ctx, task, list)], template: r.template, list };
}

/** Alias under the plan's name. */
export const saveAsTemplate = saveListAsTemplate;
